
import { Mail, MapPin, Phone, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

// Validation schema for contact form
const contactSchema = z.object({
  name: z.string().trim().min(1, "Le nom est requis").max(100, "Le nom ne peut pas dépasser 100 caractères"),
  email: z.string().trim().email("Adresse email invalide").max(255, "L'email ne peut pas dépasser 255 caractères"),
  subject: z.string().trim().min(1, "Le sujet est requis").max(200, "Le sujet ne peut pas dépasser 200 caractères"),
  message: z.string().trim().min(1, "Le message est requis").max(2000, "Le message ne peut pas dépasser 2000 caractères"),
});

const ContactSection = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitHit, setRateLimitHit] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  // Check rate limiting (max 3 submissions per hour)
  const checkRateLimit = () => {
    const storageKey = 'contact_submissions';
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    
    const submissions = JSON.parse(localStorage.getItem(storageKey) || '[]') as number[];
    const recentSubmissions = submissions.filter(time => now - time < oneHour);
    
    if (recentSubmissions.length >= 3) {
      setRateLimitHit(true);
      return false;
    }
    
    // Update submissions list
    recentSubmissions.push(now);
    localStorage.setItem(storageKey, JSON.stringify(recentSubmissions));
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset validation errors
    setValidationErrors({});
    
    // Check rate limiting
    if (!checkRateLimit()) {
      toast({
        title: "Trop de tentatives",
        description: "Vous ne pouvez envoyer que 3 messages par heure. Veuillez patienter.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const data = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      subject: formData.get('subject') as string,
      message: formData.get('message') as string,
    };

    try {
      // Validate data with zod
      const validatedData = contactSchema.parse(data);
      
      const { error } = await supabase
        .from('contact_messages')
        .insert([validatedData]);

      if (error) {
        throw error;
      }

      setIsSubmitted(true);
      toast({
        title: "Message envoyé !",
        description: "Votre message a été envoyé avec succès.",
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Handle validation errors
        const errors: Record<string, string> = {};
        error.issues.forEach((issue) => {
          if (issue.path[0]) {
            errors[issue.path[0] as string] = issue.message;
          }
        });
        setValidationErrors(errors);
        
        toast({
          title: "Erreur de validation",
          description: "Veuillez corriger les erreurs dans le formulaire.",
          variant: "destructive",
        });
      } else {
        console.error('Erreur lors de l\'envoi du message:', error);
        toast({
          title: "Erreur",
          description: "Une erreur est survenue lors de l'envoi du message.",
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };


  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      content: "saviniengicquel@gmail.com",
      href: "mailto:saviniengicquel@gmail.com"
    },
    {
      icon: Phone,
      title: "Téléphone",
      content: "+33 6 03 64 70 80",
      href: "tel:+33603647080"
    },
    {
      icon: MapPin,
      title: "Localisation",
      content: "France",
      href: "#"
    }
  ];

  return (
    <section id="contact" className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Contact <span className="text-gradient-primary">Professionnel</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Une question sur mes projets ? Intéressé par une collaboration ? 
            Contactez-moi pour discuter de vos besoins digitaux !
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Informations de contact */}
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold mb-6">Informations de contact</h3>
            
            {contactInfo.map(({ icon: Icon, title, content, href }) => (
              <Card key={title} className="hover-lift border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-full gradient-primary">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">{title}</h4>
                      <a
                        href={href}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {content}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Card className="glass-card border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Disponibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Actuellement à la recherche d'une alternance pour ma troisième année de bachelor en chef de projet digital.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Formulaire de contact */}
          <Card className="hover-lift border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">
                {isSubmitted ? "Message envoyé !" : "Envoyez-moi un message"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative overflow-hidden">
                {/* Formulaire */}
                <div className={`transition-all duration-700 ease-in-out ${
                  isSubmitted 
                    ? 'translate-x-full opacity-0 pointer-events-none' 
                    : 'translate-x-0 opacity-100'
                }`}>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium mb-2">
                          Nom
                        </label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Votre nom"
                          required
                          className={`border-2 focus:border-primary/50 ${validationErrors.name ? 'border-red-500' : ''}`}
                          disabled={isLoading}
                        />
                        {validationErrors.name && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-2">
                          Email
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="votre.email@exemple.com"
                          required
                          className={`border-2 focus:border-primary/50 ${validationErrors.email ? 'border-red-500' : ''}`}
                          disabled={isLoading}
                        />
                        {validationErrors.email && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium mb-2">
                        Sujet
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        placeholder="Sujet de votre message"
                        required
                        className={`border-2 focus:border-primary/50 ${validationErrors.subject ? 'border-red-500' : ''}`}
                        disabled={isLoading}
                      />
                      {validationErrors.subject && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.subject}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">
                        Message
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Votre message..."
                        rows={6}
                        required
                        className={`border-2 focus:border-primary/50 resize-none ${validationErrors.message ? 'border-red-500' : ''}`}
                        disabled={isLoading}
                      />
                      {validationErrors.message && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.message}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full gradient-primary hover:opacity-90 transition-opacity text-lg py-6"
                    >
                      {isLoading ? "Envoi en cours..." : "Envoyer le message"}
                    </Button>
                  </form>
                </div>

                {/* Message de succès avec animation */}
                <div className={`absolute inset-0 flex flex-col items-center justify-center text-center transition-all duration-700 ease-in-out ${
                  isSubmitted 
                    ? 'translate-x-0 opacity-100' 
                    : '-translate-x-full opacity-0 pointer-events-none'
                }`}>
                  <div className="mb-6">
                    <CheckCircle className="w-16 h-16 text-success animate-bounce mx-auto" />
                  </div>
                  <h3 className="text-2xl font-bold text-primary mb-4">
                    Message envoyé avec succès !
                  </h3>
                  <p className="text-muted-foreground max-w-md">
                    Votre message a bien été envoyé. Je vous répondrai dans les plus brefs délais.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
